import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import AppNavbar from '../components/AppNavbar';
import Header from '../components/Header';
import SideMenu from '../components/SideMenu';
import TableThumbnail from '../components/TableThumbnail';
import AppTheme from '../shared-theme/AppTheme';
import { canManageBusinessUnit } from '../utils/businessUnitAccess';
import axiosInstance from '../utils/axiosInstance';

import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from './theme/customizations';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

function normalize(value) {
  return String(value || '').trim().toLowerCase();
}

function formatLabel(value) {
  return String(value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatTimestamp(value) {
  if (!value) {
    return 'N/A';
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
}

function statusColor(value) {
  if (['online', 'running', 'available', 'normal', 'connected'].includes(value)) {
    return 'success';
  }
  if (['degraded', 'paused', 'busy', 'warning', 'reserved'].includes(value)) {
    return 'warning';
  }
  if (['offline', 'faulted', 'stopped', 'critical', 'unavailable', 'disconnected'].includes(value)) {
    return 'error';
  }
  return 'default';
}

export default function AssetsPage(props) {
  const location = useLocation();
  const partnerProfile = useSelector((state) => state.auth.partnerProfile);
  const [state, setState] = useState({ loading: true, error: '', data: { assets: [] } });
  const businessUnitId = new URLSearchParams(location.search).get('business_unit_id') || '';
  const [filters, setFilters] = useState({
    business_unit_id: businessUnitId,
    asset_type: '',
    administrative_status: '',
    operational_status: '',
    integration_status: '',
    search: '',
  });
  const [businessUnits, setBusinessUnits] = useState([]);
  const [assetTypes, setAssetTypes] = useState([]);
  const [deletingAssetId, setDeletingAssetId] = useState('');
  const [deleteFeedback, setDeleteFeedback] = useState('');

  const updateFilters = (patch) => setFilters((current) => ({ ...current, ...patch }));

  useEffect(() => {
    updateFilters({ business_unit_id: businessUnitId });
  }, [businessUnitId]);

  useEffect(() => {
    let active = true;
    async function loadFilterData() {
      try {
        const [businessUnitResponse, assetTypeResponse] = await Promise.all([
          axiosInstance.get('/partners/user/business-units'),
          axiosInstance.get('/asset-types'),
        ]);
        if (!active) {
          return;
        }
        setBusinessUnits(Array.isArray(businessUnitResponse.data?.business_units) ? businessUnitResponse.data.business_units : []);
        setAssetTypes(Array.isArray(assetTypeResponse.data?.asset_types) ? assetTypeResponse.data.asset_types : []);
      } catch (_error) {
        if (active) {
          setBusinessUnits([]);
          setAssetTypes([]);
        }
      }
    }
    loadFilterData();
    return () => {
      active = false;
    };
  }, []);

  async function loadAssets() {
    setState((current) => ({ ...current, loading: true, error: '' }));
    try {
      const params = Object.fromEntries(
        Object.entries(filters).filter(([, value]) => String(value || '').trim()),
      );
      const response = await axiosInstance.get('/partners/user/assets', {
        params,
      });
      setState({
        loading: false,
        error: '',
        data: response.data || { assets: [] },
      });
    } catch (error) {
      setState({
        loading: false,
        error: error?.response?.data?.message || error?.message || 'Unable to load assets.',
        data: { assets: [] },
      });
    }
  }

  useEffect(() => {
    loadAssets();
  }, [
    filters.business_unit_id,
    filters.asset_type,
    filters.administrative_status,
    filters.operational_status,
    filters.integration_status,
    filters.search,
  ]);

  const assets = Array.isArray(state.data?.assets) ? state.data.assets : [];
  const filteredAssets = useMemo(() => {
    const normalizedQuery = normalize(filters.search);
    if (!normalizedQuery) {
      return assets;
    }
    return assets.filter((asset) => normalize([
      asset.asset_name,
      asset.source_capability_asset_id,
      asset.business_unit_display_name,
      asset.partner_display_name,
      asset.asset_type_display_name,
      asset.location_label,
    ].filter(Boolean).join(' ')).includes(normalizedQuery));
  }, [assets, filters.search]);

  const selectedBusinessUnit = businessUnits.find((unit) => unit.business_unit_id === filters.business_unit_id);
  const addAssetPath = filters.business_unit_id
    ? `/business-units/${encodeURIComponent(filters.business_unit_id)}/assets/new`
    : '/assets/new';

  const deleteAsset = async (asset) => {
    if (!window.confirm(`Delete asset "${asset.asset_name || 'Unnamed Asset'}"? This cannot be undone.`)) {
      return;
    }
    setDeletingAssetId(asset.asset_id);
    setDeleteFeedback('');
    setState((current) => ({ ...current, error: '' }));
    try {
      await axiosInstance.delete(
        `/partners/${encodeURIComponent(asset.partner_id || asset.business_unit_id)}/assets/${encodeURIComponent(asset.asset_id)}`,
      );
      setDeleteFeedback(`${asset.asset_name || 'The asset'} was deleted.`);
      await loadAssets();
    } catch (error) {
      setState((current) => ({
        ...current,
        error: error?.response?.data?.message || error?.message || 'Unable to delete asset.',
      }));
    } finally {
      setDeletingAssetId('');
    }
  };

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <AppNavbar />

        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <Header
            breadcrumbItems={[
              { label: 'AMX xConsole', href: '/' },
              { label: 'Assets' },
            ]}
          />

          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
              <Stack spacing={0.75}>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Inventory2RoundedIcon color="primary" />
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>
                    Assets
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Registered assets visible within your Partner organization.
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip label={`${assets.length} assets`} />
                <Button
                  component={RouterLink}
                  to={addAssetPath}
                  variant="contained"
                  startIcon={<AddRoundedIcon />}
                >
                  Add Asset
                </Button>
              </Stack>
            </Stack>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5}>
                <TextField
                  value={filters.search}
                  onChange={(event) => updateFilters({ search: event.target.value })}
                  placeholder="Search Assets..."
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRoundedIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <FormControl size="small" sx={{ minWidth: { xs: '100%', lg: 220 } }}>
                  <InputLabel id="assets-business-unit-filter-label">Business Unit</InputLabel>
                  <Select
                    labelId="assets-business-unit-filter-label"
                    label="Business Unit"
                    value={filters.business_unit_id}
                    onChange={(event) => updateFilters({ business_unit_id: event.target.value })}
                  >
                    <MenuItem value="">All Business Units</MenuItem>
                    {businessUnits.map((unit) => (
                      <MenuItem key={unit.business_unit_id} value={unit.business_unit_id}>
                        {unit.display_name || unit.business_unit_code}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: { xs: '100%', lg: 180 } }}>
                  <InputLabel id="assets-type-filter-label">Type</InputLabel>
                  <Select
                    labelId="assets-type-filter-label"
                    label="Type"
                    value={filters.asset_type}
                    onChange={(event) => updateFilters({ asset_type: event.target.value })}
                  >
                    <MenuItem value="">All Types</MenuItem>
                    {assetTypes.map((type) => (
                      <MenuItem key={type.asset_type_code} value={type.asset_type_code}>
                        {type.display_name || formatLabel(type.asset_type_code)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: { xs: '100%', lg: 180 } }}>
                  <InputLabel id="assets-admin-filter-label">Lifecycle</InputLabel>
                  <Select
                    labelId="assets-admin-filter-label"
                    label="Lifecycle"
                    value={filters.administrative_status}
                    onChange={(event) => updateFilters({ administrative_status: event.target.value })}
                  >
                    <MenuItem value="">Any Lifecycle</MenuItem>
                    {['draft', 'active', 'inactive', 'retired'].map((status) => (
                      <MenuItem key={status} value={status}>{formatLabel(status)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: { xs: '100%', lg: 180 } }}>
                  <InputLabel id="assets-integration-filter-label">Integration</InputLabel>
                  <Select
                    labelId="assets-integration-filter-label"
                    label="Integration"
                    value={filters.integration_status}
                    onChange={(event) => updateFilters({ integration_status: event.target.value })}
                  >
                    <MenuItem value="">Any Integration</MenuItem>
                    {['connected', 'disconnected', 'degraded', 'unknown', 'not_configured'].map((status) => (
                      <MenuItem key={status} value={status}>{formatLabel(status)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
              {selectedBusinessUnit ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25 }}>
                  {selectedBusinessUnit.parent_partner?.display_name || selectedBusinessUnit.parent_partner?.partner_code || selectedBusinessUnit.parent_partner_id || ''}
                </Typography>
              ) : null}
            </Paper>

            {deleteFeedback ? <Alert severity="success">{deleteFeedback}</Alert> : null}

            {state.loading ? (
              <Stack direction="row" spacing={2} alignItems="center">
                <CircularProgress size={24} />
                <Typography>Loading assets...</Typography>
              </Stack>
            ) : state.error ? (
              <Alert severity="error">{state.error}</Alert>
            ) : filteredAssets.length ? (
              <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Image</TableCell>
                      <TableCell>Asset</TableCell>
                      <TableCell>Business Unit</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Updated</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredAssets.map((asset) => {
                      const canOpen = canManageBusinessUnit(
                        partnerProfile,
                        asset.business_unit_id,
                        asset.partner_id,
                      );
                      return (
                        <TableRow key={asset.asset_id} hover>
                          <TableCell sx={{ width: 74 }}>
                            <TableThumbnail
                              fileId={asset.asset_image_id}
                              alt={asset.asset_name || 'Asset image'}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {asset.asset_name || 'Unnamed Asset'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {asset.source_capability_asset_id || asset.asset_id}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {asset.business_unit_display_name || asset.business_unit_code || 'Business Unit'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {asset.partner_display_name || asset.partner_code || 'Partner'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                              <Chip size="small" color={statusColor(asset.connection_status)} label={formatLabel(asset.connection_status || 'unknown')} />
                              <Chip size="small" color={statusColor(asset.integration_status)} label={formatLabel(asset.integration_status || 'not_configured')} />
                              <Chip size="small" variant="outlined" label={asset.asset_type_display_name || formatLabel(asset.asset_type)} />
                            </Stack>
                          </TableCell>
                          <TableCell>{formatTimestamp(asset.last_seen || asset.last_status_ts || asset.updated_at)}</TableCell>
                          <TableCell align="right">
                            {canOpen ? (
                              <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <Button
                                  size="small"
                                  color="error"
                                  variant="outlined"
                                  startIcon={<DeleteRoundedIcon />}
                                  disabled={Boolean(deletingAssetId)}
                                  onClick={() => deleteAsset(asset)}
                                >
                                  {deletingAssetId === asset.asset_id ? 'Deleting...' : 'Delete'}
                                </Button>
                                <Button
                                  component={RouterLink}
                                  to={`/assets/${encodeURIComponent(asset.asset_id)}`}
                                  size="small"
                                  variant="contained"
                                  endIcon={<ChevronRightRoundedIcon />}
                                >
                                  Open
                                </Button>
                              </Stack>
                            ) : (
                              <Chip size="small" variant="outlined" label="Profile only" />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Paper>
            ) : (
              <Paper variant="outlined" sx={{ p: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  No assets match the current search.
                </Typography>
              </Paper>
            )}
          </Stack>
        </Box>
      </Box>
    </AppTheme>
  );
}
